/**
 * useProductReactions — Track quick reactions on products
 *
 * Manages reaction state (per-product), history, and comparison candidates.
 * Used by VisualPulse to prevent duplicate reactions and feed comparison mode.
 */

import { useState, useCallback, useRef } from 'react';

export function useProductReactions() {
  // Map<productId, Set<reactionType>>
  const [reactions, setReactions] = useState(() => new Map());
  // Ordered history of all reactions
  const [reactionHistory, setReactionHistory] = useState([]);
  // Products with positive reactions (more_like_this or save) — comparison candidates
  const [comparisonCandidates, setComparisonCandidates] = useState([]);

  // Debounce ref for rapid reactions (T022)
  const debounceTimerRef = useRef(null);
  const pendingReactionRef = useRef(null);

  const hasReacted = useCallback((productId, reactionType) => {
    const productReactions = reactions.get(productId);
    if (!productReactions) return false;
    return productReactions.has(reactionType);
  }, [reactions]);

  const addReaction = useCallback((productId, reactionType, productAttributes) => {
    setReactions(prev => {
      const next = new Map(prev);
      const existing = next.get(productId) || new Set();
      existing.add(reactionType);
      next.set(productId, existing);
      return next;
    });

    setReactionHistory(prev => [
      ...prev,
      { productId, reactionType, productAttributes, timestamp: Date.now() },
    ]);

    // Track comparison candidates from positive reactions
    if (reactionType === 'more_like_this' || reactionType === 'save') {
      setComparisonCandidates(prev => {
        if (prev.includes(productId)) return prev;
        return [...prev, productId];
      });
    }
  }, []);

  const getReactedProducts = useCallback(() => {
    return reactionHistory
      .filter(r => r.productAttributes)
      .reduce((acc, r) => {
        if (!acc.find(p => p.productId === r.productId)) {
          acc.push({ productId: r.productId, ...r.productAttributes });
        }
        return acc;
      }, []);
  }, [reactionHistory]);

  // T022: Debounced reaction dispatch — returns a promise that resolves
  // with the reaction to actually dispatch (or null if superseded)
  const debouncedReaction = useCallback((productId, reactionType, productAttributes, callback) => {
    // Clear any pending debounce
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    pendingReactionRef.current = { productId, reactionType, productAttributes };

    debounceTimerRef.current = setTimeout(() => {
      const pending = pendingReactionRef.current;
      if (pending) {
        pendingReactionRef.current = null;
        callback(pending);
      }
    }, 500);
  }, []);

  const comparisonReady = comparisonCandidates.length >= 2;

  const getComparisonCandidates = useCallback(() => {
    return comparisonCandidates;
  }, [comparisonCandidates]);

  return {
    reactions,
    reactionHistory,
    comparisonCandidates,
    comparisonReady,
    hasReacted,
    addReaction,
    getReactedProducts,
    getComparisonCandidates,
    debouncedReaction,
  };
}

export default useProductReactions;
